<!-- 플러그인 상세 -->
<template>
    <div class="detail-container">
        <!-- 1. 데이터 없음 표시 영역 -->
        <div class="detail-placeholder" v-if="!detailInfo">
            <span class="detail-notification-icon">
                <i class="fas fa-puzzle-piece"></i>
            </span>
            <span class="detail-notification-text">{{ $t("플러그인을 선택하세요.") }}</span>
        </div>
        <!-- 2. 상세 정보 영역 -->
        <div style="height: calc(100% - 70px); overflow: auto" v-if="detailInfo">
            <div class="detail-header" v-if="detailInfo">
                <div class="detail-title">
                    <span class="title">{{ viewTitle }}</span>
                    <span class="badge title1">{{ detailInfo.name }}</span>
                    <span :class="['badge', detailInfo.status === 'active' ? 'badge-active' : 'badge-inactive']">
                        {{ detailInfo.status === "active" ? $t("활성") : $t("비활성") }}
                    </span>
                </div>
                <div class="detail-header-buttons">
                    <template v-if="detailInfo.status !== 'active'">
                        <button type="button" class="btn btn-sm btn-success" @click="handleActivate">
                            {{ $t("활성화") }}
                        </button>
                    </template>
                    <template v-else>
                        <button type="button" class="btn btn-sm btn-warning" @click="handleDeactivate">
                            {{ $t("비활성화") }}
                        </button>
                        <button type="button" class="btn btn-sm btn-secondary" @click="handleRestart">
                            {{ $t("재시작") }}
                        </button>
                    </template>
                    <button
                        type="button"
                        class="btn btn-sm btn-secondary"
                        v-if="detailInfo"
                        @click="handleClose($event)"
                    >
                        {{ $t("닫기") }}
                    </button>
                </div>
            </div>

            <div class="detail-content" v-if="detailInfo">
                <div class="detail-section">
                    <div class="detail-section-title">{{ $t("기본 정보") }}</div>
                    <table class="detail-table">
                        <tbody>
                            <tr>
                                <th>{{ $t("이름") }}</th>
                                <td>{{ detailInfo.name }}</td>
                            </tr>
                            <tr>
                                <th>{{ $t("버전") }}</th>
                                <td>{{ detailInfo.version || "-" }}</td>
                            </tr>
                            <tr>
                                <th>{{ $t("설명") }}</th>
                                <td>{{ detailInfo.description || "-" }}</td>
                            </tr>
                            <tr>
                                <th>{{ $t("작성자") }}</th>
                                <td>{{ detailInfo.author || "-" }}</td>
                            </tr>
                            <tr>
                                <th>{{ $t("상태") }}</th>
                                <td>
                                    {{
                                        detailInfo.statusMessage ||
                                        (detailInfo.status === "active" ? $t("실행 중") : $t("중지"))
                                    }}
                                </td>
                            </tr>
                            <tr v-if="detailInfo.uptime != null && detailInfo.status === 'active'">
                                <th>{{ $t("가동 시간") }}</th>
                                <td>{{ formatUptime(detailInfo.uptime) }}</td>
                            </tr>
                            <tr v-if="detailInfo.path">
                                <th>{{ $t("설치 경로") }}</th>
                                <td>
                                    <code class="detail-path">{{ detailInfo.path }}</code>
                                </td>
                            </tr>
                            <tr v-if="pkgCategory">
                                <th>{{ $t("카테고리") }}</th>
                                <td>{{ pkgCategory }}</td>
                            </tr>
                            <tr v-if="pkgLicense">
                                <th>{{ $t("라이선스") }}</th>
                                <td>{{ pkgLicense }}</td>
                            </tr>
                            <tr v-if="pkgEngines">
                                <th>{{ $t("Node 버전") }}</th>
                                <td>{{ pkgEngines }}</td>
                            </tr>
                            <tr v-if="pkgMain">
                                <th>{{ $t("진입점") }}</th>
                                <td>
                                    <code>{{ pkgMain }}</code>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="detail-section" v-if="hasPackageMeta">
                    <div class="detail-section-title">{{ $t("패키지 정보") }}</div>
                    <table class="detail-table">
                        <tbody>
                            <tr v-if="pkgKeywords.length">
                                <th>{{ $t("키워드") }}</th>
                                <td>
                                    <span v-for="(kw, i) in pkgKeywords" :key="i" class="detail-tag">{{ kw }}</span>
                                </td>
                            </tr>
                            <tr v-if="pkgRepository">
                                <th>{{ $t("저장소") }}</th>
                                <td>
                                    <a
                                        :href="pkgRepository"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="detail-link"
                                        >{{ pkgRepository }}</a
                                    >
                                </td>
                            </tr>
                            <tr v-if="pkgHomepage">
                                <th>{{ $t("홈페이지") }}</th>
                                <td>
                                    <a
                                        :href="pkgHomepage"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="detail-link"
                                        >{{ pkgHomepage }}</a
                                    >
                                </td>
                            </tr>
                            <tr v-if="pkgDepsSummary">
                                <th>{{ $t("의존성") }}</th>
                                <td>{{ pkgDepsSummary }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="detail-section" v-if="detailInfo.config && Object.keys(detailInfo.config).length">
                    <div class="detail-section-title">{{ $t("설정") }}</div>
                    <pre class="detail-config">{{ JSON.stringify(detailInfo.config, null, 2) }}</pre>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import * as popupMessages from "@src/consts/popupMessageConsts";

export default {
    components: {},
    props: {
        title: { type: String, default: () => "플러그인" },
        pluginName: { type: String, default: null },
    },
    data() {
        return {
            detailInfo: null,
            mode: "view",
        };
    },
    computed: {
        viewTitle() {
            return this.title;
        },
        pkg() {
            return this.detailInfo?.packageJson || {};
        },
        pkgCategory() {
            return this.pkg.category || "";
        },
        pkgLicense() {
            return this.pkg.license || "";
        },
        pkgEngines() {
            const e = this.pkg.engines;
            if (!e) return "";
            return typeof e === "string" ? e : e.node || "";
        },
        pkgMain() {
            return this.pkg.main || "";
        },
        pkgKeywords() {
            const k = this.pkg.keywords;
            return Array.isArray(k) ? k : [];
        },
        pkgRepository() {
            const r = this.pkg.repository;
            if (!r) return "";
            return typeof r === "string" ? r : r.url || "";
        },
        pkgHomepage() {
            return this.pkg.homepage || "";
        },
        pkgDepsSummary() {
            const d = this.pkg.dependencies;
            const dev = this.pkg.devDependencies;
            if (!d && !dev) return "";
            const depCount = d ? Object.keys(d).length : 0;
            const devCount = dev ? Object.keys(dev).length : 0;
            if (depCount && devCount) return `${this.$t("런타임")} ${depCount}개, ${this.$t("개발")} ${devCount}개`;
            if (depCount) return `${this.$t("런타임")} ${depCount}개`;
            return `${this.$t("개발")} ${devCount}개`;
        },
        hasPackageMeta() {
            return this.pkgKeywords.length > 0 || this.pkgRepository || this.pkgHomepage || this.pkgDepsSummary;
        },
    },
    watch: {
        pluginName() {
            if (!this.pluginName) {
                this.clearData();
            } else {
                this.loadData();
            }
        },
    },
    methods: {
        async notifyCancelEdit() {
            if (this.mode === "edit") {
                const result = await this.alertConfirmWarning("편집중인 내용을 무시하고 종료할까요?");
                return !!result.value;
            }
            return true;
        },

        formatUptime(ms) {
            if (ms == null || typeof ms !== "number") return "-";
            const sec = Math.floor(ms / 1000);
            const min = Math.floor(sec / 60);
            const hour = Math.floor(min / 60);
            const day = Math.floor(hour / 24);
            if (day > 0) return `${day}일 ${hour % 24}시간`;
            if (hour > 0) return `${hour}시간 ${min % 60}분`;
            if (min > 0) return `${min}분 ${sec % 60}초`;
            return `${sec}초`;
        },

        async loadData() {
            if (!this.pluginName) return;
            try {
                const result = await backEndApi.pluginManage.getPlugin(this.pluginName);
                const body = result.data;
                if (result.status === 200 && body?.data) {
                    this.detailInfo = { ...body.data };
                    this.mode = "view";
                } else {
                    this.alertWarning(
                        body?.message || "조회 실패",
                        "",
                        typeof body?.detail === "string" ? body.detail : ""
                    );
                }
            } catch (e) {
                this.alertNoti(popupMessages.COMMON_NO_SEARCH_DATA_POPUP_MESSAGE || "데이터를 불러올 수 없습니다.");
                console.error(e);
            }
        },

        clearData() {
            this.detailInfo = null;
            this.mode = "view";
            this.$forceUpdate();
        },

        async handleActivate() {
            if (!this.pluginName) return;
            try {
                const res = await backEndApi.pluginManage.activatePlugin(this.pluginName);
                if (res.status === 200 && res.data?.success !== false && !this.$err(res?.data)) {
                    this.alertNoti(this.$t("활성화되었습니다."));
                    await this.loadData();
                    this.$emit("data-updated");
                } else {
                    this.alertWarning(res.data?.message || this.$t("활성화 실패"), "", res.data?.detail);
                }
            } catch (e) {
                console.error(e);
                this.alertWarning(this.$t("활성화 중 오류가 발생했습니다."));
            }
        },

        async handleDeactivate() {
            if (!this.pluginName) return;
            const result = await this.alertConfirmWarning(
                this.$t("플러그인을 비활성화하면 해당 기능을 사용할 수 없습니다. 계속할까요?")
            );
            if (!result?.value) return;
            try {
                const res = await backEndApi.pluginManage.deactivatePlugin(this.pluginName);
                if (res.status === 200 && res.data?.success !== false && !this.$err(res?.data)) {
                    this.alertNoti(this.$t("비활성화되었습니다."));
                    await this.loadData();
                    this.$emit("data-updated");
                } else {
                    this.alertWarning(res.data?.message || this.$t("비활성화 실패"), "", res.data?.detail);
                }
            } catch (e) {
                console.error(e);
                this.alertWarning(this.$t("비활성화 중 오류가 발생했습니다."));
            }
        },

        async handleRestart() {
            if (!this.pluginName) return;
            const result = await this.alertConfirmWarning(
                this.$t("플러그인을 재시작하면 일시적으로 해당 기능이 중단됩니다. 계속할까요?")
            );
            if (!result?.value) return;
            try {
                const res = await backEndApi.pluginManage.restartPlugin(this.pluginName);
                if (res.status === 200 && res.data?.success !== false && !this.$err(res?.data)) {
                    this.alertNoti(this.$t("재시작되었습니다."));
                    await this.loadData();
                    this.$emit("data-updated");
                } else {
                    this.alertWarning(res.data?.message || this.$t("재시작 실패"), "", res.data?.detail);
                }
            } catch (e) {
                console.error(e);
                this.alertWarning(this.$t("재시작 중 오류가 발생했습니다."));
            }
        },

        async handleClose(event) {
            const ok = await this.notifyCancelEdit();
            if (!ok) {
                if (event) event.preventDefault();
                return false;
            }
            this.clearData();
            this.$emit("closed");
            return true;
        },
    },
};
</script>

<style scoped>
.detail-container {
    height: 100%;
    width: 100%;
    padding: 1rem;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    font-size: 12px;
}

.detail-placeholder {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.detail-notification-icon {
    padding: 1rem;
    color: #444;
    font-size: 48px;
}

.detail-notification-text {
    font-size: 15px;
    font-weight: bold;
}

.detail-header {
    margin-bottom: 0;
    padding: 8px 16px;
    overflow: hidden;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 6px 6px 0 0;
    background-color: #f1f1f1;
    border: 1px solid #ececec;
    border-bottom: 1px solid #ccc;
    font-weight: bold;
}

.detail-header .detail-title {
    padding: 0;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
}

.detail-header-buttons {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-left: auto;
}

.detail-content {
    margin-top: 2px;
    padding: 16px;
    overflow: auto;
    background-color: white;
    border: 1px solid #ececec;
    border-radius: 0 0 6px 6px;
}

.detail-section {
    margin-bottom: 16px;
}

.detail-section-title {
    font-weight: 600;
    margin-bottom: 8px;
}

.detail-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}

.detail-table th,
.detail-table td {
    border: 1px solid #eee;
    padding: 6px 10px;
    text-align: left;
}

.detail-table th {
    width: 120px;
    background: #f9f9f9;
}

.detail-config {
    font-size: 11px;
    padding: 10px;
    background: #f5f5f5;
    border: 1px solid #eee;
    border-radius: 4px;
    overflow: auto;
    margin: 0;
}

.detail-path {
    font-size: 11px;
    word-break: break-all;
    display: inline-block;
    max-width: 100%;
}

.detail-tag {
    display: inline-block;
    margin: 2px 4px 2px 0;
    padding: 2px 8px;
    background: #e9ecef;
    border-radius: 4px;
    font-size: 11px;
}

.detail-link {
    word-break: break-all;
    color: #007bff;
}

.detail-link:hover {
    text-decoration: underline;
}

.title {
    font-weight: 600;
    margin-right: 12px;
}

.badge {
    font-size: 12px;
    font-weight: 500;
    margin-right: 4px;
    display: inline-block;
    min-width: 20px;
    padding: 0 10px;
    color: #fff;
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
    line-height: 22px;
    border-radius: 8px;
}

.badge.title1 {
    background-color: #ff6d00;
}

.badge.badge-active {
    background-color: #28a745;
}

.badge.badge-inactive {
    background-color: #6c757d;
}
</style>
